import type { APIComponentType, ArdoqId } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import { uniqBy } from 'lodash';
import {
  DIMENSION_TO_DATA_KEY,
  FIELD_INFO_KEYS,
  type BubbleChartDataPointModel,
  type BubbleChartViewSettings,
  type ComponentTypeRegistry,
  type FieldData,
  type FieldInfoKey,
  type FieldNameAndLabel,
} from './types';
import { isBubbleChartStringField } from './predicates';
import { isFieldAvailableInCurrentContext } from './labelFilterValidation';

export type MinMaxRange = {
  min: number;
  max: number;
};

export type ComponentTypesById = {
  name: string;
  ids: ArdoqId[];
};

export type BubbleChartDimensionRanges = Record<FieldInfoKey, MinMaxRange>;

export type BubbleChartLabelFilterTarget = {
  dimension: FieldInfoKey;
  fieldName: string;
  range: MinMaxRange;
};

/**
 * The values and bounds the bubble chart settings controls reason over. Each
 * environment builds its own settings controls dependencies shape on top of
 * this.
 */
export type BubbleChartSettingsBounds = {
  /** Selecting a type by name stores every id that shares that name. */
  componentTypesById: ComponentTypesById[];
  selectedComponentTypes: ArdoqId[];
  componentAxisFieldNames: string[];
  isReferenceMode: boolean;
  /** Whether the field source the axes currently read from offers any field. */
  hasFieldsInCurrentMode: boolean;
  canSetComponentTypes: boolean;
  canSetComponentAxisFields: boolean;
  dimensionRanges: BubbleChartDimensionRanges;
  labelFilterTargets: BubbleChartLabelFilterTarget[];
  canSetLabelFilter: boolean;
};

/**
 * The bounds the AI view settings context publishes. The JSDoc on
 * `SettableBubbleChartViewSettings` points the model at these by name, so a key
 * removed here leaves those descriptions naming something absent.
 */
export const BUBBLE_CHART_AI_VIEW_STATE_KEYS = [
  'canSetComponentTypes',
  'componentTypesById',
  'canSetComponentAxisFields',
  'componentAxisFieldNames',
  'canSetLabelFilter',
  'labelFilterTargets',
] as const satisfies readonly (keyof BubbleChartSettingsBounds)[];

export const toComponentTypesById = (
  componentTypes: APIComponentType[]
): ComponentTypesById[] =>
  Array.from(
    componentTypes.reduce(
      (idsByName, { name, id }) =>
        idsByName.set(name, [...(idsByName.get(name) ?? []), id]),
      new Map<string, ArdoqId[]>()
    ),
    ([name, ids]) => ({ name, ids })
  );

export const toSelectedComponentTypes = (
  componentTypes: APIComponentType[],
  selectedComponentTypes: ArdoqId[]
): ArdoqId[] =>
  componentTypes.some(({ id }) => selectedComponentTypes.includes(id))
    ? selectedComponentTypes
    : [WILDCARD];

const toDimensionRange = (
  data: BubbleChartDataPointModel[],
  dimension: FieldInfoKey
): MinMaxRange => {
  const dataKey = DIMENSION_TO_DATA_KEY[dimension];
  const { min, max } = data.reduce(
    (range, dataPoint) => {
      const value = dataPoint[dataKey];
      return Number.isFinite(value)
        ? {
            min: Math.min(range.min, value),
            max: Math.max(range.max, value),
          }
        : range;
    },
    { min: Infinity, max: -Infinity }
  );

  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
};

const dimensionRangesByData = new WeakMap<
  BubbleChartDataPointModel[],
  BubbleChartDimensionRanges
>();

const toDimensionRanges = (
  data: BubbleChartDataPointModel[]
): BubbleChartDimensionRanges => {
  const cached = dimensionRangesByData.get(data);
  if (cached) {
    return cached;
  }

  const dimensionRanges = {
    selectedFieldNameX: toDimensionRange(data, 'selectedFieldNameX'),
    selectedFieldNameY: toDimensionRange(data, 'selectedFieldNameY'),
    selectedFieldNameRadius: toDimensionRange(data, 'selectedFieldNameRadius'),
  };
  dimensionRangesByData.set(data, dimensionRanges);

  return dimensionRanges;
};

type ToLabelFilterTargetsArgs = {
  viewSettings: BubbleChartViewSettings;
  dimensionRanges: BubbleChartDimensionRanges;
  numericFields: FieldData[];
  availableBubbleComponentFields: FieldNameAndLabel[];
  availableReferenceFields: FieldNameAndLabel[];
  availableReferencedComponentFields: FieldNameAndLabel[];
};

const toLabelFilterTargets = ({
  viewSettings,
  dimensionRanges,
  numericFields,
  availableBubbleComponentFields,
  availableReferenceFields,
  availableReferencedComponentFields,
}: ToLabelFilterTargetsArgs): BubbleChartLabelFilterTarget[] =>
  FIELD_INFO_KEYS.map(dimension => ({
    dimension,
    fieldName: viewSettings[dimension],
    range: dimensionRanges[dimension],
  })).filter(
    (target): target is BubbleChartLabelFilterTarget =>
      isBubbleChartStringField(target.fieldName) &&
      isFieldAvailableInCurrentContext(target.fieldName, numericFields, {
        availableBubbleComponentFields,
        availableReferenceFields,
        availableReferencedComponentFields,
      })
  );

type BubbleChartSettingsBoundsSource = {
  viewSettings: BubbleChartViewSettings;
  data: BubbleChartDataPointModel[];
  numericFields: FieldData[];
  availableBubbleComponentFields: FieldNameAndLabel[];
  availableReferenceFields: FieldNameAndLabel[];
  availableReferencedComponentFields: FieldNameAndLabel[];
  componentTypeRegistry: ComponentTypeRegistry | null;
  /**
   * Whether the axes read reference fields. Viewpoint mode additionally gates
   * this on the reference bubble chart feature; workspace mode does not.
   */
  isReferenceMode: boolean;
};

export const toBubbleChartSettingsBounds = ({
  viewSettings,
  data,
  numericFields,
  availableBubbleComponentFields,
  availableReferenceFields,
  availableReferencedComponentFields,
  componentTypeRegistry,
  isReferenceMode,
}: BubbleChartSettingsBoundsSource): BubbleChartSettingsBounds => {
  const componentTypes = Array.from(
    componentTypeRegistry?.values() ?? [],
    ({ componentType }) => componentType
  );
  const componentAxisFieldNames = uniqBy(
    numericFields.filter(field => !field.isConfidential),
    'name'
  ).map(({ name }) => name);
  const dimensionRanges = toDimensionRanges(data);
  const labelFilterTargets = toLabelFilterTargets({
    viewSettings,
    dimensionRanges,
    numericFields,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
  });

  return {
    componentTypesById: toComponentTypesById(componentTypes),
    selectedComponentTypes: toSelectedComponentTypes(
      componentTypes,
      viewSettings.selectedComponentTypes
    ),
    componentAxisFieldNames,
    isReferenceMode,
    hasFieldsInCurrentMode: isReferenceMode
      ? availableReferenceFields.length > 0 ||
        availableReferencedComponentFields.length > 0 ||
        availableBubbleComponentFields.length > 0
      : numericFields.length > 0,
    canSetComponentTypes: componentTypeRegistry !== null,
    canSetComponentAxisFields:
      !isReferenceMode && componentAxisFieldNames.length > 0,
    dimensionRanges,
    labelFilterTargets,
    canSetLabelFilter: labelFilterTargets.length > 0,
  };
};
